import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';
import { SettingStore } from '../../../stores/setting.store';

interface Language {
  code: string;
  label: string;
  flag: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  imports: [RouterLink, RouterLinkActive, TranslateModule],
})
export class HeaderComponent {
  protected readonly settingStore = inject(SettingStore);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly langChange = toSignal(this.translateService.onLangChange);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );
  protected readonly defaultLogoUrl = '/default-logo.png';

  protected mobileMenuOpen = signal(false);

  readonly languages: Language[] = [
    { code: 'ko-KR', label: '한국어', flag: 'icon-[circle-flags--kr]' },
    { code: 'en-US', label: 'English', flag: 'icon-[circle-flags--us]' },
    { code: 'ja-JP', label: '일본어', flag: 'icon-[circle-flags--jp]' },
  ];

  currentLang = signal<Language>(this.languages[0]);

  protected readonly isHomeRoute = computed(() => {
    const [path] = this.currentUrl().split(/[?#]/);
    return path === '' || path === '/';
  });

  readonly navItems = computed<NavItem[]>(() => {
    this.langChange();
    return [
      {
        label: this.translateService.instant('LAYOUT.NAV.HOME'),
        path: '/',
        icon: 'icon-[mdi--home-outline]',
      },
      {
        label: this.translateService.instant('LAYOUT.NAV.ABOUT'),
        path: '/about',
        icon: 'icon-[mdi--office-building-outline]',
      },
      {
        label: this.translateService.instant('LAYOUT.NAV.PRODUCT'),
        path: '/product',
        icon: 'icon-[mdi--package-variant-closed]',
      },
      {
        label: this.translateService.instant('LAYOUT.NAV.CUSTOMER_SERVICE'),
        path: '/customer',
        icon: 'icon-[mdi--headset]',
      },
    ];
  });

  constructor() {
    const savedLang = localStorage.getItem('language');
    const lang = this.languages.find(l => l.code === savedLang);
    if (lang) {
      this.currentLang.set(lang);
    }
  }

  changeLanguage(lang: Language): void {
    this.currentLang.set(lang);
    this.translateService.use(lang.code);
    localStorage.setItem('language', lang.code);
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update(v => !v);
  }

  protected onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.src.endsWith(this.defaultLogoUrl)) {
      img.style.visibility = 'hidden';
      return;
    }

    img.src = this.defaultLogoUrl;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }
}
